import { toast } from "sonner";

// Cache para prevenir notificaciones duplicadas
type NotificationCache = {
  [key: string]: {
    timestamp: number;
    count: number;
  };
};

const NOTIFICATION_CACHE: NotificationCache = {};
const DEFAULT_COOLDOWN = 3000; // 3 segundos para la mayoría
const ERROR_COOLDOWN = 5000; // 5 segundos para errores

// Diccionario de mensajes contextuales
const MESSAGES = {
  sync: {
    success: "Datos sincronizados correctamente",
    error: "Error al sincronizar datos",
    offline: "Sin conexión - los cambios se guardarán cuando vuelvas a estar online"
  },
  routine: {
    created: "Rutina creada exitosamente",
    updated: "Rutina actualizada",
    deleted: "Rutina eliminada",
    error: "Error al procesar la rutina"
  },
  exercise: {
    created: "Ejercicio agregado",
    updated: "Ejercicio actualizado",
    deleted: "Ejercicio eliminado",
    reordered: "Ejercicios reordenados",
    error: "Error al procesar el ejercicio"
  },
  auth: {
    loginSuccess: "Sesión iniciada correctamente",
    logoutSuccess: "Sesión cerrada",
    loginError: "Error al iniciar sesión",
    unauthorized: "No tienes permisos para esta acción"
  },
  general: {
    saved: "Cambios guardados",
    loading: "Procesando...",
    error: "Ha ocurrido un error inesperado"
  }
};

const generateKey = (message: string, type: string): string => {
  return `${type}:${message}`;
};

const isDuplicate = (key: string, cooldownTime: number): boolean => {
  const now = Date.now();
  const cached = NOTIFICATION_CACHE[key];
  
  if (cached && now - cached.timestamp < cooldownTime) {
    NOTIFICATION_CACHE[key] = {
      timestamp: now,
      count: cached.count + 1
    };
    return true;
  }
  
  NOTIFICATION_CACHE[key] = {
    timestamp: now,
    count: 1
  };
  
  return false;
};

interface NotificationOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  description?: string;
}

export const useNotifications = () => {
  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' | 'loading',
    options: NotificationOptions = {}
  ) => {
    const key = generateKey(message, type);
    const cooldown = type === 'error' ? ERROR_COOLDOWN : DEFAULT_COOLDOWN;
    
    if (isDuplicate(key, cooldown)) {
      return;
    }

    const defaultDurations = {
      success: 3000,
      error: 6000,
      warning: 4000,
      info: 4000,
      loading: 0 // No auto-dismiss
    };

    const toastOptions = {
      description: options.description,
      action: options.action,
      duration: options.duration || defaultDurations[type],
    };

    switch (type) {
      case 'success':
        return toast.success(message, toastOptions);
      case 'error':
        return toast.error(message, toastOptions);
      case 'warning':
        return toast.warning(message, toastOptions);
      case 'info':
        return toast.info(message, toastOptions);
      case 'loading':
        return toast.loading(message, toastOptions);
    }
  };

  // Métodos de conveniencia con mensajes predefinidos
  const success = (message: string, options?: NotificationOptions) => 
    showNotification(message, 'success', options);

  const error = (message: string, options?: NotificationOptions) => 
    showNotification(message, 'error', options);

  const warning = (message: string, options?: NotificationOptions) => 
    showNotification(message, 'warning', options);

  const info = (message: string, options?: NotificationOptions) => 
    showNotification(message, 'info', options);

  const loading = (message: string, options?: NotificationOptions) => 
    showNotification(message, 'loading', options);

  // Métodos contextuales con mensajes predefinidos
  const sync = {
    success: () => success(MESSAGES.sync.success),
    error: () => error(MESSAGES.sync.error),
    offline: () => warning(MESSAGES.sync.offline)
  };

  const routine = {
    created: () => success(MESSAGES.routine.created),
    updated: () => success(MESSAGES.routine.updated),
    deleted: () => success(MESSAGES.routine.deleted),
    error: () => error(MESSAGES.routine.error)
  };

  const exercise = {
    created: () => success(MESSAGES.exercise.created),
    updated: () => success(MESSAGES.exercise.updated),
    deleted: () => success(MESSAGES.exercise.deleted),
    reordered: () => success(MESSAGES.exercise.reordered),
    error: () => error(MESSAGES.exercise.error)
  };

  const auth = {
    loginSuccess: () => success(MESSAGES.auth.loginSuccess),
    logoutSuccess: () => success(MESSAGES.auth.logoutSuccess),
    loginError: () => error(MESSAGES.auth.loginError),
    unauthorized: () => error(MESSAGES.auth.unauthorized)
  };

  // Limpiar cache (útil para testing)
  const clearCache = () => {
    Object.keys(NOTIFICATION_CACHE).forEach(key => {
      delete NOTIFICATION_CACHE[key];
    });
  };

  // Dismiss todas las notificaciones
  const dismissAll = () => toast.dismiss();

  return {
    // Métodos básicos
    success,
    error,
    warning,
    info,
    loading,
    
    // Métodos contextuales
    sync,
    routine,
    exercise,
    auth,
    
    // Utilidades
    clearCache,
    dismissAll,
    
    // Método genérico para casos especiales
    show: showNotification
  };
};